import type { ChangelogAudience, ChangelogEntry } from '@good-season/schema';

/**
 * Set when Reload is about to apply a waiting version. The next boot reads it
 * and may show What's new. Cleared once that boot decides, including when
 * there is nothing to say. A failed read of the change log leaves it set, so
 * a gym with no signal can show the note later.
 */
export const UPDATE_ARMED_KEY = 'gs.update.armed';

/** Newest change-log id this device has already been shown in the note. */
export const WHATS_NEW_VERSION_KEY = 'gs.whatsNew.version';

/** Cap on the note. Fewer than this is fine when the deploy changed less. */
export const WHATS_NEW_MAX = 5;

const COACH_ROLES = new Set([
  'owner',
  'head_coach',
  'assistant_coach',
  'team_manager',
  'scorekeeper',
  'club_admin',
]);

/** True on the live book, its setup, and the box score. The note waits. */
export function isScorebookPath(pathname: string): boolean {
  return pathname === '/scorebook' || pathname.startsWith('/scorebook/');
}

/**
 * Who this device may be told about.
 *
 * `everyone` always. A guardian also sees parent items. Any staff role also
 * sees coach items. An unknown role sees only `everyone`, which under-shows
 * rather than handing a parent a coach note.
 */
export function audiencesFor(roles: readonly string[]): ReadonlySet<ChangelogAudience> {
  const allowed = new Set<ChangelogAudience>(['everyone']);
  for (const role of roles) {
    if (role === 'guardian') allowed.add('parents');
    if (COACH_ROLES.has(role)) allowed.add('coaches');
  }
  return allowed;
}

export function rolesFromMemberships(body: unknown): readonly string[] {
  if (typeof body !== 'object' || body === null) return [];
  const list = (body as { memberships?: unknown }).memberships;
  if (!Array.isArray(list)) return [];
  return list.flatMap((item) => {
    if (typeof item !== 'object' || item === null) return [];
    const role = (item as { role?: unknown }).role;
    return typeof role === 'string' ? [role] : [];
  });
}

export function visibleEntries(
  entries: readonly ChangelogEntry[],
  roles: readonly string[],
): readonly ChangelogEntry[] {
  const allowed = audiencesFor(roles);
  return entries.filter((entry) => allowed.has(entry.audience));
}

/**
 * The bullets for this boot, newest first, at most five.
 *
 * Entries strictly newer than the id stored last time. A first note, or a
 * stored id the file no longer has, uses the newest few rather than the whole
 * history. Empty when the stored id is already the newest visible entry, or
 * when the file has nothing this person should see.
 */
export function bulletsFor(
  visible: readonly ChangelogEntry[],
  seenVersion: string | null,
): readonly ChangelogEntry[] {
  if (visible.length === 0) return [];
  if (seenVersion === null) return visible.slice(0, WHATS_NEW_MAX);
  const at = visible.findIndex((entry) => entry.id === seenVersion);
  if (at === 0) return [];
  const newer = at < 0 ? visible : visible.slice(0, at);
  return newer.slice(0, WHATS_NEW_MAX);
}
